import type { ComponentType, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  Zap,
  UserRound,
  Phone,
  MonitorSmartphone,
  LineChart,
  LayoutGrid,
} from 'lucide-react'

import type { OrgMetricsSummary } from '@/hooks/useOrgMetrics'
import type { PhoneLineSummary } from '@/features/phone-lines/types'

import { formatUtcMonth } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useShellHref } from '@/utils/router'
import { Skeleton } from '@/components/ui/skeleton'
import { Card } from '@/components/ui/card'

import { WorkerGlyphs } from './WorkerGlyphs'

type CardConfig = {
  id: string
  label: string
  /** Shown beside the label, in a lighter weight. */
  subtitle?: string
  value: number
  Icon: ComponentType<{ className?: string }>
  chipClassName: string
  iconClassName: string
  labelClassName: string
  valueClassName: string
  /** When set, the card becomes a link to this href and gains a hover lift. */
  to?: string
  /** Span both grid columns (the FTW card sits on its own row). */
  fullSpan?: boolean
  /** Extra content rendered below the value (the FTW worker-icon row). */
  children?: ReactNode
  /** Per-card loading override (else the shared `isLoading` is used). */
  loading?: boolean
}

type CountCardDef = {
  key: keyof OrgMetricsSummary
  label: string
  url: string
  Icon: ComponentType<{ className?: string }>
  chipClassName: string
  iconClassName: string
  labelClassName: string
  valueClassName: string
}

const COUNT_CARDS: ReadonlyArray<CountCardDef> = [
  {
    key: 'duties_count',
    label: 'Duties',
    url: '/duties',
    Icon: Zap,
    chipClassName: 'bg-red-100 dark:bg-red-950',
    iconClassName: 'text-red-500 dark:text-red-400',
    labelClassName: 'text-red-500 dark:text-red-400',
    valueClassName: 'text-red-500 dark:text-red-400',
  },
  {
    key: 'patients_count',
    label: 'Clients',
    url: '/data/patients',
    Icon: UserRound,
    chipClassName: 'bg-fuchsia-100 dark:bg-fuchsia-950',
    iconClassName: 'text-fuchsia-500 dark:text-fuchsia-400',
    labelClassName: 'text-fuchsia-500 dark:text-fuchsia-400',
    valueClassName: 'text-fuchsia-500 dark:text-fuchsia-400',
  },
  {
    key: 'calls_count',
    label: 'Calls',
    url: '/calls',
    Icon: Phone,
    chipClassName: 'bg-violet-100 dark:bg-violet-950',
    iconClassName: 'text-violet-500 dark:text-violet-400',
    labelClassName: 'text-slate-800 dark:text-slate-100',
    valueClassName: 'text-violet-600 dark:text-violet-400',
  },
  {
    key: 'computer_use_sessions_count',
    label: 'Computer Use',
    url: '/sessions',
    Icon: MonitorSmartphone,
    chipClassName: 'bg-slate-100 dark:bg-slate-800',
    iconClassName: 'text-slate-700 dark:text-slate-200',
    labelClassName: 'text-slate-800 dark:text-slate-100',
    valueClassName: 'text-slate-900 dark:text-slate-50',
  },
]

export function SectionCards({
  summary,
  ftwEstimate,
  ftwPeriodStart,
  isLoading,
  phoneLineSummary,
  phoneLineLoading,
}: {
  summary?: OrgMetricsSummary
  ftwEstimate?: number | null
  /** ISO date of the first day of the month `ftwEstimate` covers. */
  ftwPeriodStart?: string
  isLoading?: boolean
  /** Super-user-only global phone-line pool; omitted for non-super users. */
  phoneLineSummary?: PhoneLineSummary
  phoneLineLoading?: boolean
}) {
  const shellHref = useShellHref()
  // Render the Phone Lines card only for Careforce-org admins: the query is
  // disabled otherwise, so both data and loading stay falsy for everyone else.
  // (The pool is Careforce-wide, and the board this links to only renders there.)
  const showPhoneLines = phoneLineSummary !== undefined || !!phoneLineLoading

  const cards: CardConfig[] = COUNT_CARDS.map((c) => ({
    id: c.key,
    label: c.label,
    value: summary?.[c.key] ?? 0,
    Icon: c.Icon,
    chipClassName: c.chipClassName,
    iconClassName: c.iconClassName,
    labelClassName: c.labelClassName,
    valueClassName: c.valueClassName,
    to: shellHref(c.url),
  }))

  // FTW and Active Lines share a bottom row (below the 4 count cards): FTW
  // takes the width, Active Lines is a narrow card inline beside it. Round to
  // whole workers so the glyph count matches the printed number, and hide the
  // FTW card when there is no usage / a zero estimate.
  const workers = ftwEstimate ? Math.round(ftwEstimate) : 0
  const ftwCard: CardConfig | null =
    workers > 0
      ? {
          id: 'ftw_estimate',
          label: 'Full Time Workers Est.',
          subtitle: ftwPeriodStart ? formatUtcMonth(ftwPeriodStart) : undefined,
          value: workers,
          Icon: LineChart,
          chipClassName: 'bg-blue-100 dark:bg-blue-950',
          iconClassName: 'text-blue-600 dark:text-blue-400',
          labelClassName: 'text-blue-700 dark:text-blue-400',
          valueClassName: 'text-blue-600 dark:text-blue-400',
          // The estimate is derived from usage, so the card links to the
          // Usage page (/billing) it is computed from.
          to: shellHref('/billing'),
          children: <WorkerGlyphs workers={workers} />,
        }
      : null

  // Active Lines reuses the same card renderer as the count cards (cyan, its own
  // loading flag, links to the board) so the shell can't drift from them.
  const activeLinesCard: CardConfig | null = showPhoneLines
    ? {
        id: 'active_lines',
        label: 'Active Lines',
        value: phoneLineSummary?.active ?? 0,
        Icon: LayoutGrid,
        chipClassName: 'bg-cyan-100 dark:bg-cyan-950',
        iconClassName: 'text-cyan-600 dark:text-cyan-400',
        labelClassName: 'text-cyan-700 dark:text-cyan-400',
        valueClassName: 'text-cyan-600 dark:text-cyan-400',
        to: shellHref('/phone-lines'),
        loading: phoneLineLoading,
      }
    : null

  const renderStatCard = ({
    id,
    label,
    subtitle,
    value,
    Icon,
    chipClassName,
    iconClassName,
    labelClassName,
    valueClassName,
    to,
    fullSpan,
    children,
    loading,
  }: CardConfig) => {
    const showSkeleton = loading ?? isLoading
    const card = (
      <Card
        className={cn(
          'gap-2 rounded-2xl p-4 shadow-[var(--shadow-card-elevated)]',
          to &&
            'transition-[translate,box-shadow] duration-fast ease-smooth-out hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-elevated-hover)]'
        )}>
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-sm',
              chipClassName
            )}>
            <Icon className={cn('size-4', iconClassName)} aria-hidden />
          </span>
          <span className={cn('truncate text-sm font-medium', labelClassName)}>
            {label}
          </span>
          {subtitle && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {subtitle}
            </span>
          )}
        </div>
        {showSkeleton ? (
          <Skeleton className="h-9 w-24" />
        ) : (
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={cn(
                'min-w-0 truncate text-4xl font-semibold tabular-nums',
                valueClassName
              )}
              title={value.toLocaleString()}>
              {value.toLocaleString()}
            </span>
            {children}
          </div>
        )}
      </Card>
    )
    const wrapperClass = cn('block min-w-0', fullSpan && '@md/cards:col-span-2')
    return to ? (
      <Link key={id} to={to} className={wrapperClass}>
        {card}
      </Link>
    ) : (
      <div key={id} className={wrapperClass}>
        {card}
      </div>
    )
  }

  // Without an FTW card there's no wide bottom row to sit beside, so Active
  // Lines joins the main grid and matches the other cards' column width. With
  // FTW present it rides beside it as a narrow card in the bottom row.
  const gridCards =
    !ftwCard && activeLinesCard ? [...cards, activeLinesCard] : cards

  return (
    <div className="@container/cards flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 @md/cards:grid-cols-2">
        {gridCards.map(renderStatCard)}
      </div>

      {/* Bottom row: FTW (fills the width) + a narrow Active Lines card beside
          it. Only rendered when FTW exists — otherwise Active Lines is in the
          grid above. */}
      {ftwCard && (
        <div className="flex flex-col gap-3 @md/cards:flex-row @md/cards:items-start">
          <div className="min-w-0 flex-1">{renderStatCard(ftwCard)}</div>
          {activeLinesCard && (
            <div className="min-w-0 @md/cards:w-44 @md/cards:shrink-0">
              {renderStatCard(activeLinesCard)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
